import { Injectable, OnDestroy } from '@angular/core';
import { UserRoleEnum } from '@shared/enums';
import {
    CanAccessGroupConditions,
    CanDeleteGroupConditions,
    CanEditGeneralSettingsConditions,
    CanEditUserRoleConditions,
    CanInviteNewMemberConditions,
    CanLeaveGroupConditions,
    CanRemoveMemberConditions,
    CanRestoreGroupConditions,
    CanTransferGroupOwnershipConditions,
    RequesterUser,
} from '@shared/interfaces';
import { Subscription } from 'rxjs';
import { GroupMemberRole } from 'src/app/features/groups/group-settings/_core/enums';
import { initializeGroupMemberRolesList } from 'src/app/features/groups/group-settings/_core/helpers';
import { CurrentUserService } from './current-user.service';

@Injectable({ providedIn: 'root' })
export class RolesAndPermissionsService implements OnDestroy {
    private readonly subscriptions = new Subscription();

    private transnetyxGlobalRole: UserRoleEnum | null = null;
    private currentUserId: number | null = null;

    constructor(private currentUserService: CurrentUserService) {
        this.subscriptions.add(
            this.currentUserService.currentUser$.subscribe(currentUser => {
                this.transnetyxGlobalRole = currentUser?.role ?? null;
                this.currentUserId = currentUser?.id ?? null;
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    canAccessGroup(conditions: CanAccessGroupConditions) {
        if (this.transnetyxGlobalRole === UserRoleEnum.ADMIN) return true;
        if (conditions.isGroupOwner) return true;
        if (conditions.userRoleInGroup === GroupMemberRole.PI) return true;
        if (
            (conditions.userRoleInGroup === GroupMemberRole.GROUP_MANAGER ||
                conditions.userRoleInGroup === GroupMemberRole.RESEARCHER) &&
            conditions.hasJoinedGroup &&
            conditions.isGroupActive
        ) {
            return true;
        }

        return false;
    }

    canLeaveGroup(conditions: CanLeaveGroupConditions) {
        if (conditions.isGroupOwner) return false;
        if (conditions.isGroupActive === false) return false;
        if (conditions.role === null) return false;

        return true;
    }

    canDeleteGroup(conditions: CanDeleteGroupConditions) {
        if (conditions.isGroupActive === false) return false;
        if (this.transnetyxGlobalRole === UserRoleEnum.ADMIN) return true;
        if (conditions.isGroupOwner) return true;
        if (conditions.userRoleInGroup === GroupMemberRole.PI) return true;

        return false;
    }

    canRestoreGroup(conditions: CanRestoreGroupConditions) {
        if (conditions.isGroupActive) return false;
        if (this.transnetyxGlobalRole === UserRoleEnum.ADMIN) return true;
        if (conditions.isGroupOwner) return true;
        if (conditions.userRoleInGroup === GroupMemberRole.PI) return true;

        return false;
    }

    canTransferOwnership(conditions: CanTransferGroupOwnershipConditions) {
        if (conditions.isGroupActive === false) return false;
        if (conditions.numberOfGroupMembers !== null && conditions.numberOfGroupMembers <= 1) return false;
        if (conditions.numberOfGroupMembers === null && conditions.canGroupBeTransferredInitially === false) {
            return false;
        }
        if (this.transnetyxGlobalRole === UserRoleEnum.ADMIN) return true;
        if (conditions.isGroupOwner) return true;

        return false;
    }

    canEditGeneralSettings(conditions: CanEditGeneralSettingsConditions) {
        if (conditions.isGroupActive === false) return false;
        if (conditions.isChangingGeneralDetails) return false;
        if (this.transnetyxGlobalRole === UserRoleEnum.ADMIN) return true;
        if (conditions.isGroupOwner) return true;
        if (
            conditions.userRoleInGroup === GroupMemberRole.GROUP_MANAGER ||
            conditions.userRoleInGroup === GroupMemberRole.PI
        ) {
            return true;
        }

        return false;
    }

    canInviteNewMembers(conditions: CanInviteNewMemberConditions) {
        if (conditions.isGroupActive === false) return false;
        if (this.transnetyxGlobalRole === UserRoleEnum.ADMIN) return true;
        if (conditions.isGroupOwner) return true;
        if (
            conditions.userRoleInGroup === GroupMemberRole.GROUP_MANAGER ||
            conditions.userRoleInGroup === GroupMemberRole.PI
        ) {
            return true;
        }

        return false;
    }

    canRemoveMember(conditions: CanRemoveMemberConditions) {
        if (conditions.isGroupActive === false) return false;
        if (conditions.selectedUserDetails.isGroupOwner) return false;
        if (conditions.selectedUserDetails.isTransnetyxAdmin) return false;
        if (conditions.selectedUserDetails.selectedUserIsCurrentUser) return false;
        if (this.transnetyxGlobalRole === UserRoleEnum.ADMIN) return true;
        if (conditions.isGroupOwner) return true;
        if (
            conditions.currentUserRoleInGroup === GroupMemberRole.GROUP_MANAGER ||
            conditions.currentUserRoleInGroup === GroupMemberRole.PI
        ) {
            return true;
        }

        return false;
    }

    canEditMemberRole(conditions: CanEditUserRoleConditions) {
        if (conditions.isGroupActive === false) return false;
        if (
            conditions.selectedUserDetails.isTransnetyxAdmin &&
            conditions.selectedUserDetails.selectedUserIsCurrentUser === false
        ) {
            return false;
        }
        if (this.transnetyxGlobalRole === UserRoleEnum.ADMIN) return true;
        if (conditions.isGroupOwner) return true;
        if (
            conditions.currentUserRoleInGroup === GroupMemberRole.GROUP_MANAGER ||
            conditions.currentUserRoleInGroup === GroupMemberRole.PI
        ) {
            return true;
        }

        return false;
    }

    generatePossibleRolesOptions(conditions: { isGroupOwner: boolean }) {
        if (conditions.isGroupOwner) {
            return initializeGroupMemberRolesList().filter(
                el => el.value !== GroupMemberRole.ALL && el.value !== GroupMemberRole.RESEARCHER
            );
        }

        return initializeGroupMemberRolesList().filter(el => el.value != GroupMemberRole.ALL);
    }

    showCurrentUserAsAccountOwner(conditions: { requesters: RequesterUser[] }): boolean {
        if (this.transnetyxGlobalRole === UserRoleEnum.USER) true;
        if (this.currentUserId === null) return false;
        return conditions.requesters.some(el => el.id === this.currentUserId);
    }

    canChangeToMainGroup(conditions: { isGroupActive: boolean; userRoleInGroup: GroupMemberRole | null }): boolean {
        if (!conditions.isGroupActive) return false;
        if (this.transnetyxGlobalRole === UserRoleEnum.ADMIN) return true;
        if (conditions.userRoleInGroup !== null) return true;

        return false;
    }

    canSwitchToGroup(conditions: {
        isGroupDeleted: boolean;
        canAccessGroupDetails: boolean;
        isGroupCurrentlySelected: boolean;
        hasJoinedGroup: boolean;
    }): boolean {
        if (conditions.isGroupDeleted) return false;
        if (conditions.isGroupCurrentlySelected) return false;
        if (this.transnetyxGlobalRole === UserRoleEnum.ADMIN) return true;
        if (conditions.hasJoinedGroup) return true;

        return false;
    }

    canJoinGroup(conditions: { isGroupActive: boolean; userRoleInGroup: GroupMemberRole | null }): boolean {
        if (!conditions.isGroupActive) return false;
        if (conditions.userRoleInGroup !== null) return false;
        if (this.transnetyxGlobalRole !== UserRoleEnum.ADMIN) return false;

        return true;
    }

    canAccessPricing(): boolean {
        if (this.transnetyxGlobalRole !== UserRoleEnum.ADMIN) return false;

        return true;
    }
}
